import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';
import Modal from '../components/Modal';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const ControlInventory = () => {
    const { authFetch, depos, user } = useContext(AppContext);
    const { t } = useTranslation();
    const [selectedDepo, setSelectedDepo] = useState('');
    const [stocks, setStocks] = useState([]);
    const [physicalQty, setPhysicalQty] = useState({});
    const [search, setSearch] = useState('');
    const [submitStatus, setSubmitStatus] = useState(null); // null | 'pending' | 'submitted'
    const [pendingDrafts, setPendingDrafts] = useState([]);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [selectedHistoryGroupId, setSelectedHistoryGroupId] = useState(null);
    const [selectedDraftGroupId, setSelectedDraftGroupId] = useState(null);
    const [showPrint, setShowPrint] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const fileInputRef = useRef(null);
    const printRef = useRef(null);

    const fetchStocks = useCallback(() => {
        if (!selectedDepo) return;
        authFetch(`/api/stock?depo_id=${selectedDepo}`)
            .then(r => r.json())
            .then(data => setStocks(Array.isArray(data) ? data : []));
    }, [selectedDepo, authFetch]);

    useEffect(() => {
        fetchStocks();
        setPhysicalQty({});
        setSubmitStatus(null);
    }, [selectedDepo, fetchStocks]);

    // Trigger popup print
    useEffect(() => {
        if (isPrinting) {
            const depoLabel = depos.find(d => d.id == selectedDepo)?.name || selectedDepo;
            const dataRows = displayStocks.map((s, i) => {
                const sys = Number(s.quantity);
                const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : sys;
                const diff = phy - sys;
                const price = parseFloat(s.purchase_price || 0);
                const total = sys * price;
                const isMatch = sys === phy;
                return `<tr style="background:${i%2===0?'#f8fafc':'#fff'}">
                    <td style="padding:5px 8px;font-weight:700">${s.product_name}</td>
                    <td style="padding:5px 8px;font-family:monospace;font-size:9px;color:#9ca3af">${s.sku||'—'}</td>
                    <td style="padding:5px 8px;font-family:monospace;font-size:9px;color:#9ca3af">${s.barcode||'—'}</td>
                    <td style="padding:5px 8px;text-align:center;font-weight:700;color:#1d4ed8">${sys}</td>
                    <td style="padding:5px 8px;text-align:center;font-weight:700;color:#059669">${phy}</td>
                    <td style="padding:5px 8px;text-align:center;font-weight:700;color:${diff===0?'#6b7280':diff>0?'#2563eb':'#dc2626'}">${diff>0?'+':''}${diff}</td>
                    <td style="padding:5px 8px;text-align:right">${price.toFixed(2)} CFA</td>
                    <td style="padding:5px 8px;text-align:right;font-weight:700">${total.toFixed(2)} CFA</td>
                    <td style="padding:5px 8px;text-align:center;font-weight:700;color:${isMatch?'#059669':'#dc2626'}">${isMatch?'Vrai':'Faux'}</td>
                </tr>`;
            }).join('');
            const totSys = displayStocks.reduce((s,r)=>s+Number(r.quantity),0);
            const totVal = displayStocks.reduce((s,r)=>s+Number(r.quantity)*parseFloat(r.purchase_price||0),0);
            const missingVal = totalMissingValue;
            const html = `<!DOCTYPE html><html><head>
                <meta charset="utf-8"><title>Inventory Control Report — ${depoLabel}</title>
                <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; } @page { size: A4 landscape; margin: 10mm; }
                table { border-collapse: collapse; width: 100%; } th { padding: 8px; font-size: 10px; font-weight: 900; text-transform: uppercase; color: #6b7280; background: #f9fafb; text-align: left; border-bottom: 2px solid #1f2937; }
                td { border-bottom: 1px solid #f1f5f9; }
                </style>
            </head><body>
                <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #312e81;padding-bottom:10px;margin-bottom:14px">
                    <div><div style="font-size:20px;font-weight:900;text-transform:uppercase;color:#312e81">INVENTORY CONTROL REPORT</div>
                    <div style="font-size:10px;color:#6b7280;margin-top:2px;font-weight:700">Warehouse: ${depoLabel}</div></div>
                    <div style="text-align:right;font-size:10px;color:#6b7280">
                        <div>Total System Value: <b>${totalSystemValue.toFixed(2)} CFA</b></div>
                        <div>Total Physical Value: <b>${totalPhysicalValue.toFixed(2)} CFA</b></div>
                        <div style="color:#dc2626">Missing/Loss: <b>${missingVal.toFixed(2)} CFA</b> (${lossPercent}%)</div>
                        <div>Generated: <b>${new Date().toLocaleString()}</b></div>
                    </div>
                </div>
                <div className="w-full overflow-x-auto max-w-full">
<table><thead><tr>
                    <th>Product Name</th><th>Reference</th><th>Barcode</th>
                    <th style="text-align:center">Qty (System)</th><th style="text-align:center">Qty (Physical)</th>
                    <th style="text-align:center">Diff</th><th style="text-align:right">Price</th>
                    <th style="text-align:right">Total</th><th style="text-align:center">Vrai/Faux</th>
                </tr></thead><tbody>${dataRows}</tbody>
                <tfoot><tr style="background:#eef2ff;font-weight:900;border-top:2px solid #312e81">
                    <td colspan="3" style="padding:8px;font-weight:900;color:#312e81">TOTALS</td>
                    <td style="padding:8px;text-align:center">${totSys}</td>
                    <td colspan="3"></td>
                    <td style="padding:8px;text-align:right;font-weight:900">${totVal.toFixed(2)} CFA</td>
                    <td></td>
                </tr></tfoot></table>
</div>
                <div style="margin-top:32px;display:flex;justify-content:space-around">
                    ${['Warehouse Supervisor','Inventory Control','Authorized Signature'].map(l=>`<div style="text-align:center;width:28%"><div style="border-bottom:1px solid #334155;height:28px;margin-bottom:4px"></div><div style="font-size:9px;color:#64748b;font-weight:700">${l}</div></div>`).join('')}
                </div>
            <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>
            </body></html>`;
            const popup = window.open('', '_blank', 'width=1050,height=750,scrollbars=yes');
            if (popup) { popup.document.write(html); popup.document.close(); }
            else alert('Please allow popups for this site to print.');
            setIsPrinting(false);
        }
    }, [isPrinting]);

    const handlePhysicalQtyChange = (stockId, value) => {
        setPhysicalQty(prev => ({ ...prev, [stockId]: value }));
        setSubmitStatus(null);
    };

    // Submit physical counts for admin review
    const handleSubmitForReview = async () => {
        const rows = stocks.map(s => ({
            stock_id: s.id,
            depo_id: selectedDepo,
            old_physical_qty: Number(s.quantity),
            new_physical_qty: Number(physicalQty[s.id] ?? s.quantity),
            changed_by: user?.name || user?.username || '',
            change_reason: 'Physical count from Control page'
        }));
        try {
            const res = await authFetch('/api/stock?action=save_draft', {
                method: 'POST',
                body: JSON.stringify({ rows })
            });
            if (res.ok) {
                setSubmitStatus('submitted');
            } else {
                alert('Error submitting. Please try again.');
            }
        } catch {
            alert('Network error. Please try again.');
        }
    };

    // Admin: load pending drafts
    const handleViewDrafts = async () => {
        try {
            const res = await authFetch(`/api/stock?action=draft_history&depo_id=${selectedDepo}`);
            const data = await res.json();
            setPendingDrafts(Array.isArray(data) ? data.filter(d => !d.status || d.status === 'Pending') : []);
            setShowDraftModal(true);
        } catch {
            alert('Failed to load drafts.');
        }
    };

    const handleAcceptDraft = async (draftId, stockId, newQty) => {
        await authFetch('/api/stock?action=accept_draft', {
            method: 'POST',
            body: JSON.stringify({ draft_id: draftId, stock_id: stockId, new_physical_qty: newQty })
        });
        setPendingDrafts(prev => prev.filter(d => d.id !== draftId));
        fetchStocks();
    };

    const handleRejectDraft = async (draftId) => {
        await authFetch('/api/stock?action=reject_draft', {
            method: 'POST',
            body: JSON.stringify({ draft_id: draftId })
        });
        setPendingDrafts(prev => prev.filter(d => d.id !== draftId));
    };

    // Export to Excel — French format with full styling (ExcelJS)
    const handleExportExcel = async () => {
        const depoLabel = depos.find(d => d.id == selectedDepo)?.name || selectedDepo;
        const dateStr = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');

        const wb = new ExcelJS.Workbook();
        wb.creator = 'ERP System';
        wb.created = new Date();
        const ws = wb.addWorksheet('Inventaire', { views: [{ state: 'frozen', ySplit: 2 }] });

        // ── Column definitions ──────────────────────────────────────
        ws.columns = [
            { key: 'ref', width: 16 },
            { key: 'des', width: 38 },
            { key: 'barcode', width: 20 },
            { key: 'qteSys', width: 14 },
            { key: 'qtePhy', width: 14 },
            { key: 'diff', width: 12 },
            { key: 'prix', width: 12 },
            { key: 'total', width: 16 },
            { key: 'vraifaux', width: 14 },
        ];

        // ── Row 1: Title ────────────────────────────────────────────
        const titleRow = ws.addRow([
            `INVENTAIRE ${depoLabel.toUpperCase()} ${dateStr}`,
            '', '', '',
            totalMissingValue > 0 ? -totalMissingValue : '',
            '', '', '', ''
        ]);
        ws.mergeCells('A1:D1');
        titleRow.height = 22;
        titleRow.eachCell({ includeEmpty: true }, cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true, size: 12 };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
        // Missing value cell (E1) — red
        const e1 = titleRow.getCell(5);
        if (totalMissingValue > 0) {
            e1.numFmt = '#,##0.00';
            e1.font = { color: { argb: 'FFDC2626' }, bold: true, size: 12 };
        }

        // ── Row 2: Headers ──────────────────────────────────────────
        const headers = ['REF', 'DES', 'CODE BARRE', 'QTE SYS', 'QTE PHY', 'DIFF', 'PRIX', 'TOTAL', 'VRAI/FAUX'];
        const headerRow = ws.addRow(headers);
        headerRow.height = 18;
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2FF' } };
            cell.font = { bold: true, color: { argb: 'FF312E81' }, size: 10 };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FFC7D2FE' } },
                bottom: { style: 'medium', color: { argb: 'FF4338CA' } },
                left: { style: 'thin', color: { argb: 'FFC7D2FE' } },
                right: { style: 'thin', color: { argb: 'FFC7D2FE' } },
            };
        });

        // ── Data Rows ───────────────────────────────────────────────
        displayStocks.forEach((s, i) => {
            const rowNum = i + 3; // rows start at 3 (1=title, 2=header)
            const sys = Number(s.quantity);
            const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : sys;
            const price = parseFloat(s.purchase_price || 0);
            const isVrai = sys === phy;
            const bgColor = i % 2 === 0 ? 'FFFFFFFF' : 'FFF9FAFB';

            const row = ws.addRow([
                s.sku || '',
                s.product_name,
                s.barcode || '',
                sys,
                phy,
                { formula: `E${rowNum}-D${rowNum}` },   // DIFF formula
                price,
                { formula: `D${rowNum}*G${rowNum}` },   // TOTAL formula
                isVrai ? 'VRAI' : 'FAUX',
            ]);

            row.height = 16;
            row.eachCell({ includeEmpty: true }, cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                    bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                    left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                    right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
                };
            });

            // DES column: left-aligned
            row.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };

            // DIFF cell: color based on value
            const diffCell = row.getCell(6);
            if (sys === phy) {
                diffCell.font = { color: { argb: 'FF6B7280' } };
            } else if (phy > sys) {
                diffCell.font = { color: { argb: 'FF2563EB' }, bold: true };
            } else {
                diffCell.font = { color: { argb: 'FFDC2626' }, bold: true };
                // Highlight missing rows in faint red
                row.eachCell({ includeEmpty: true }, c => {
                    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF5F5' } };
                });
            }

            // PRIX + TOTAL: number format
            row.getCell(7).numFmt = '#,##0.00';
            row.getCell(8).numFmt = '#,##0.00';

            // VRAI/FAUX: green or red fill + bold
            const vfCell = row.getCell(9);
            if (isVrai) {
                vfCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
                vfCell.font = { color: { argb: 'FF059669' }, bold: true };
            } else {
                vfCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
                vfCell.font = { color: { argb: 'FFDC2626' }, bold: true };
            }
        });

        // ── Summary row at the bottom ────────────────────────────────
        ws.addRow([]);
        const lastDataRow = displayStocks.length + 2;
        const summaryRow = ws.addRow([
            'TOTAL', '',
            `Missing: CFA${totalMissingValue.toFixed(2)}`,
            { formula: `SUM(D3:D${lastDataRow})` },
            { formula: `SUM(E3:E${lastDataRow})` },
            { formula: `SUM(F3:F${lastDataRow})` },
            '',
            { formula: `SUM(H3:H${lastDataRow})` },
            `Loss: ${lossPercent}%`,
        ]);
        summaryRow.height = 18;
        summaryRow.eachCell({ includeEmpty: true }, cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2FF' } };
            cell.font = { bold: true, color: { argb: 'FF312E81' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
        summaryRow.getCell(7).numFmt = '#,##0.00';
        summaryRow.getCell(8).numFmt = '#,##0.00';
        summaryRow.getCell(6).font = { bold: true, color: { argb: 'FFDC2626' } };

        // ── Download ─────────────────────────────────────────────────
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Inventaire_${depoLabel}_${dateStr}.xlsx`);
    };

    // Import Excel — compatible with the French-format export (REF/QTE SYS/QTE PHY)
    const handleImportExcel = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const wb = XLSX.read(evt.target.result, { type: 'binary' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            // Skip row 0 (title) and use row 1 as header
            const rows = XLSX.utils.sheet_to_json(ws, { defval: '', range: 1 });
            const newPhysical = {};
            let count = 0;
            rows.forEach(r => {
                // French format: REF column matches SKU
                const ref = r['REF'] ?? r['Stock ID'] ?? null;
                const phyQty = r['QTE PHY'] ?? r['QTY (Physical)'] ?? r['Physical Qty'] ?? r['Physical Stock'];
                if (ref && phyQty !== '' && phyQty !== undefined) {
                    // Find stock by SKU (REF)
                    const stock = stocks.find(s => String(s.sku) === String(ref) || String(s.id) === String(ref));
                    if (stock) {
                        newPhysical[stock.id] = Number(phyQty);
                        count++;
                    }
                }
            });
            setPhysicalQty(prev => ({ ...prev, ...newPhysical }));
            setSubmitStatus(null);
            alert(`Imported ${count} physical quantities from Excel.`);
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };


    // ── History Tab Logic ─────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('control'); // 'control' | 'history'
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyFilter, setHistoryFilter] = useState('all'); // all, today, week, month, range
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    const loadHistory = useCallback(async () => {
        if (!selectedDepo) return;
        setIsHistoryLoading(true);
        try {
            let url = `/api/stock?action=draft_history&depo_id=${selectedDepo}&filter=${historyFilter}`;
            if (historyFilter === 'range' && dateRange.start && dateRange.end) {
                url += `&start=${dateRange.start}&end=${dateRange.end}`;
            }
            const res = await authFetch(url);
            const data = await res.json();
            setHistoryLogs(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsHistoryLoading(false);
        }
    }, [selectedDepo, historyFilter, dateRange, authFetch]);

    useEffect(() => {
        if (activeTab === 'history') {
            loadHistory();
        }
    }, [activeTab, loadHistory]);

    // Computed Properties & Grouping Logic
    const groupBySubmission = (logs) => {
        const map = new Map();
        logs.forEach(log => {
            // Group by minute to bundle simultaneous inserts
            const d = new Date(log.created_at);
            d.setSeconds(0, 0);
            const key = `${d.getTime()}_${log.changed_by}`;
            if (!map.has(key)) {
                map.set(key, {
                    id: key,
                    created_at: log.created_at, // keep actual time
                    changed_by: log.changed_by || 'Admin',
                    status: log.status,
                    total_items: 0,
                    updated_items: 0,
                    items: []
                });
            }
            const group = map.get(key);
            group.items.push(log);
            group.total_items += 1;
            const diff = Number(log.new_physical_qty) - Number(log.old_physical_qty);
            if (diff !== 0) group.updated_items += 1;

            // Re-evaluate group status
            if (log.status === 'Pending') group.status = 'Pending';
            else if (log.status === 'Rejected' && group.status !== 'Pending') group.status = 'Rejected';
        });
        return Array.from(map.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    };

    const groupedHistory = groupBySubmission(historyLogs);
    const groupedDrafts = groupBySubmission(pendingDrafts);

    const selectedHistoryGroup = groupedHistory.find(g => g.id === selectedHistoryGroupId);
    const selectedDraftGroup = groupedDrafts.find(g => g.id === selectedDraftGroupId);

    const displayStocks = stocks.filter(s =>
        s.product_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.sku?.toLowerCase().includes(search.toLowerCase())
    );

    const totalSystemValue = stocks.reduce((sum, s) => sum + Number(s.quantity) * parseFloat(s.purchase_price || 0), 0);
    const totalPhysicalValue = stocks.reduce((sum, s) => {
        const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : Number(s.quantity);
        return sum + phy * parseFloat(s.purchase_price || 0);
    }, 0);
    const totalMissingValue = stocks.reduce((sum, s) => {
        const sys = Number(s.quantity);
        const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : sys;
        const diff = sys - phy;
        return sum + (diff > 0 ? diff * parseFloat(s.purchase_price || 0) : 0);
    }, 0);
    const lossPercent = totalSystemValue > 0 ? ((totalMissingValue / totalSystemValue) * 100).toFixed(2) : '0.00';
    const missingItems = stocks.filter(s => {
        const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : Number(s.quantity);
        return Number(s.quantity) > phy;
    });

    const depoName = depos.find(d => d.id == selectedDepo)?.name || selectedDepo;

    return (
        <div className="flex flex-col h-[calc(100vh-4rem)]">
            {/* ── Header ────────────────────────────────── */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 shrink-0">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">📋 {t('Control Inventory')}</h2>
                    <p className="text-sm text-gray-500 mt-1">{t('Physical count verification & history')}</p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold text-gray-600">{t('Warehouse')}:</label>
                    <select
                        value={selectedDepo}
                        onChange={e => setSelectedDepo(e.target.value)}
                        className="border rounded-lg px-3 py-2 bg-gray-50 focus:ring-2 focus:ring-indigo-400 outline-none text-sm"
                    >
                        <option value="">— {t('Select Warehouse')} —</option>
                        {depos && depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </div>
            </div>

            {!selectedDepo ? (
                <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                    <span className="text-6xl mb-4">🏢</span>
                    <p className="text-lg font-semibold">{t('Select a warehouse to begin')}</p>
                </div>
            ) : (
                <>
                    {/* ── Tabs ─────────────────────────────── */}
                    <div className="flex border-b border-gray-200 mb-5 gap-6 shrink-0">
                        <button
                            onClick={() => setActiveTab('control')}
                            className={`pb-3 font-semibold text-sm transition-colors border-b-2 ${activeTab === 'control' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <span className="mr-2">📝</span>{t('Count & Adjust')}
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`pb-3 font-semibold text-sm transition-colors border-b-2 ${activeTab === 'history' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <span className="mr-2">🕒</span>{t('Audit History Log')}
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0">
                        {activeTab === 'control' ? (
                            <>
                                {/* ── Status Banner ────────────────────── */}
                                {submitStatus === 'submitted' && (
                                    <div className="mb-4 bg-yellow-50 border border-yellow-300 text-yellow-800 px-5 py-3 rounded-lg flex items-center gap-3 font-semibold shrink-0">
                                        ⏳ Submitted — waiting for Admin acceptance. Stock will update once approved.
                                    </div>
                                )}

                                {/* ── Toolbar ──────────────────────────── */}
                                <div className="flex flex-wrap gap-3 mb-5 shrink-0">
                                    <button
                                        onClick={() => setIsPrinting(true)}
                                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors"
                                    >
                                        🖨️ {t('Print Report')}
                                    </button>
                                    <button
                                        onClick={handleExportExcel}
                                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors"
                                    >
                                        ⬇️ {t('Export Excel', 'Export Excel')}
                                    </button>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors"
                                    >
                                        ⬆️ {t('Import Excel', 'Import Excel')}
                                    </button>
                                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportExcel} />

                                    <button
                                        onClick={handleSubmitForReview}
                                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors"
                                    >
                                        ✅ {t('Submit for Review')}
                                    </button>

                                    {user?.role === 'Admin' && (
                                        <button
                                            onClick={handleViewDrafts}
                                            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors"
                                        >
                                            🔍 {t('Review Pending Drafts')}
                                        </button>
                                    )}
                                </div>

                                {/* ── Summary Cards ─────────────────────── */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 shrink-0">
                                    <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                                        <div className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1">{t('Theoretical Stock')}</div>
                                        <div className="text-xl font-extrabold text-indigo-800">CFA{totalSystemValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    </div>
                                    <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-4">
                                        <div className="text-xs text-emerald-600 font-bold uppercase tracking-wider mb-1">{t('Physical Count')}</div>
                                        <div className="text-xl font-extrabold text-emerald-800">CFA{totalPhysicalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    </div>
                                    <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
                                        <div className="text-xs text-red-600 font-bold uppercase tracking-wider mb-1">{t('Missing / Loss')}</div>
                                        <div className="text-xl font-extrabold text-red-700">CFA{totalMissingValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    </div>
                                    <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
                                        <div className="text-xs text-orange-600 font-bold uppercase tracking-wider mb-1">{t('Loss %')}</div>
                                        <div className="text-xl font-extrabold text-orange-700">{lossPercent}%</div>
                                    </div>
                                </div>

                                {/* ── Search ────────────────────────────── */}
                                <div className="mb-4 shrink-0">
                                    <input
                                        type="text"
                                        placeholder={t('Search by product or SKU...')}
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="border rounded-lg px-4 py-2 w-80 bg-gray-50 focus:ring-2 focus:ring-indigo-300 outline-none text-sm"
                                    />
                                </div>

                                {/* ── Main Table ────────────────────────── */}
                                <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 shrink-0">
                                    <div className="w-full overflow-x-auto max-w-full">
<table className="min-w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50 text-gray-500 text-xs font-bold uppercase tracking-wider border-b border-gray-100">
                                                <th className="px-4 py-3 text-left">Stock ID</th>
                                                <th className="px-4 py-3 text-left">{t('Product')}</th>
                                                <th className="px-4 py-3 text-left">{t('Reference')}</th>
                                                <th className="px-4 py-3 text-left">{t('Barcode')}</th>
                                                <th className="px-4 py-3 text-left">{t('Warehouse')}</th>
                                                <th className="px-4 py-3 text-center">{t('Theoretical Stock')}</th>
                                                <th className="px-4 py-3 text-center">{t('Physical Count')}</th>
                                                <th className="px-4 py-3 text-center">{t('Difference')}</th>
                                                <th className="px-4 py-3 text-right">{t('Price')}</th>
                                                <th className="px-4 py-3 text-right">{t('Total')}</th>
                                                <th className="px-4 py-3 text-center">✓</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {displayStocks.length === 0 ? (
                                                <tr>
                                                    <td colSpan="11" className="px-4 py-12 text-center text-gray-400 italic">
                                                        {stocks.length === 0 ? 'No stock found for this warehouse.' : 'No results match your search.'}
                                                    </td>
                                                </tr>
                                            ) : displayStocks.map(s => {
                                                const sys = Number(s.quantity);
                                                const phyRaw = physicalQty[s.id];
                                                const phy = phyRaw !== undefined ? Number(phyRaw) : sys;
                                                const diff = phy - sys;
                                                const price = parseFloat(s.purchase_price || 0);
                                                const total = sys * price;
                                                const isMatch = sys === phy;
                                                return (
                                                    <tr key={s.id} className={`hover:bg-gray-50 transition ${!isMatch ? 'bg-red-50' : ''}`}>
                                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{s.id}</td>
                                                        <td className="px-4 py-3 font-semibold text-gray-800">{s.product_name}</td>
                                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{s.sku || '—'}</td>
                                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{s.barcode || '—'}</td>
                                                        <td className="px-4 py-3 text-gray-500 text-xs">{depoName}</td>
                                                        <td className="px-4 py-3 text-center font-bold text-blue-700">{sys}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <input
                                                                type="number"
                                                                className="w-20 border border-gray-200 rounded px-2 py-1 text-xs text-center focus:ring-2 focus:ring-indigo-300 outline-none"
                                                                value={physicalQty[s.id] ?? ''}
                                                                onChange={e => handlePhysicalQtyChange(s.id, e.target.value)}
                                                                placeholder="Enter..."
                                                            />
                                                        </td>
                                                        <td className={`px-4 py-3 text-center font-bold ${diff === 0 ? 'text-gray-400' : diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                                            {phyRaw !== undefined ? (diff > 0 ? `+${diff}` : diff) : '0'}
                                                        </td>
                                                        <td className="px-4 py-3 text-right text-gray-600">CFA{price.toFixed(2)}</td>
                                                        <td className="px-4 py-3 text-right font-semibold text-gray-700">CFA{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                        <td className={`px-4 py-3 text-center font-bold text-sm ${isMatch ? 'text-emerald-600' : 'text-red-600'}`}>
                                                            {isMatch ? 'Vrai' : 'Faux'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
</div>
                                </div>
                            </>
                        ) : (
                            /* ── History View ────────────────────────────── */
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 shrink-0">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-gray-100 pb-4">
                                    <h3 className="font-bold text-gray-800 text-lg">Physical Count Log</h3>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <select
                                            value={historyFilter}
                                            onChange={e => setHistoryFilter(e.target.value)}
                                            className="border rounded-lg px-3 py-1.5 bg-gray-50 focus:ring-2 focus:ring-indigo-300 outline-none text-sm font-semibold text-gray-700"
                                        >
                                            <option value="all">All Time</option>
                                            <option value="today">Today</option>
                                            <option value="week">Last 7 Days</option>
                                            <option value="month">This Month</option>
                                            <option value="range">Custom Range...</option>
                                        </select>

                                        {historyFilter === 'range' && (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="date"
                                                    value={dateRange.start}
                                                    onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
                                                    className="border rounded px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-indigo-300"
                                                />
                                                <span className="text-gray-400">to</span>
                                                <input
                                                    type="date"
                                                    value={dateRange.end}
                                                    onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
                                                    className="border rounded px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-indigo-300"
                                                />
                                                <button
                                                    onClick={loadHistory}
                                                    className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1 rounded text-xs font-bold transition-colors"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {isHistoryLoading ? (
                                    <div className="py-12 text-center text-gray-400">Loading history...</div>
                                ) : historyLogs.length === 0 ? (
                                    <div className="py-12 text-center text-gray-400 italic">No history found for the selected date range.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 text-gray-500 text-xs uppercase font-bold text-left border-b">
                                                    <th className="px-4 py-3">Date / Time</th>
                                                    <th className="px-4 py-3">Submitted By</th>
                                                    <th className="px-4 py-3 text-center">Total Products</th>
                                                    <th className="px-4 py-3 text-center">Updated Products</th>
                                                    <th className="px-4 py-3 text-center">Status</th>
                                                    <th className="px-4 py-3 text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {groupedHistory.map(group => (
                                                    <tr key={group.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-xs text-gray-500">
                                                            {new Date(group.created_at).toLocaleString('en-GB')}
                                                        </td>
                                                        <td className="px-4 py-3 font-semibold text-gray-800">
                                                            {group.changed_by}
                                                        </td>
                                                        <td className="px-4 py-3 text-center text-gray-500">{group.total_items}</td>
                                                        <td className="px-4 py-3 text-center font-bold text-indigo-700">{group.updated_items}</td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${group.status === 'Accepted' ? 'bg-emerald-100 text-emerald-700' :
                                                                group.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                                                    'bg-yellow-100 text-yellow-700'
                                                                }`}>
                                                                {group.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button
                                                                onClick={() => setSelectedHistoryGroupId(group.id)}
                                                                className="text-xs bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold py-1 px-3 rounded transition-colors"
                                                            >
                                                                📄 View Details
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Admin: Review Pending Drafts Modal ── */}
                    <Modal isOpen={showDraftModal} onClose={() => setShowDraftModal(false)} title="Pending Physical Count Submissions" size="xl">
                        {groupedDrafts.length === 0 ? (
                            <div className="py-10 text-center text-gray-400 italic">No pending submissions for this warehouse.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-500 text-xs uppercase font-bold border-b">
                                            <th className="px-4 py-2 text-left">Date / Time</th>
                                            <th className="px-4 py-2 text-left">Submitted By</th>
                                            <th className="px-4 py-2 text-center">Updated Products</th>
                                            <th className="px-4 py-2 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {groupedDrafts.map(group => (
                                            <tr key={group.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 text-xs text-gray-500">{new Date(group.created_at).toLocaleString('en-GB')}</td>
                                                <td className="px-4 py-2 font-semibold text-gray-800">{group.changed_by}</td>
                                                <td className="px-4 py-2 text-center font-bold text-indigo-700">{group.updated_items} / {group.total_items}</td>
                                                <td className="px-4 py-2 text-center flex justify-center gap-2">
                                                    <button
                                                        onClick={() => setSelectedDraftGroupId(group.id)}
                                                        className="bg-indigo-600 border border-indigo-700 hover:bg-indigo-700 text-white font-bold py-1 px-3 rounded text-xs transition-colors"
                                                    >
                                                        🔍 Review Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Modal>

                    {/* ── History Details Modal ── */}
                    <Modal isOpen={!!selectedHistoryGroup} onClose={() => setSelectedHistoryGroupId(null)} title={`Submission Details - ${selectedHistoryGroup?.changed_by}`} size="2xl">
                        {selectedHistoryGroup && (
                            <div className="overflow-x-auto max-h-[60vh] border rounded-lg">
                                <table className="min-w-full text-sm border-collapse">
                                    <thead className="sticky top-0 bg-gray-50 shadow-sm">
                                        <tr className="text-gray-500 text-xs uppercase font-bold text-left border-b">
                                            <th className="px-4 py-3">Product</th>
                                            <th className="px-4 py-3 text-center">Previous Qty</th>
                                            <th className="px-4 py-3 text-center">New Qty</th>
                                            <th className="px-4 py-3 text-center">Diff</th>
                                            <th className="px-4 py-3 text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {selectedHistoryGroup.items.map(log => {
                                            const diff = Number(log.new_physical_qty) - Number(log.old_physical_qty);
                                            const isUpdated = diff !== 0;
                                            return (
                                                <tr key={log.id} className={isUpdated ? 'bg-indigo-50/30' : 'bg-white'}>
                                                    <td className="px-4 py-3 font-semibold text-gray-800">{log.product_name || `ID #${log.stock_id}`}</td>
                                                    <td className="px-4 py-3 text-center text-gray-500">{Number(log.old_physical_qty)}</td>
                                                    <td className="px-4 py-3 text-center font-bold text-indigo-700">{Number(log.new_physical_qty)}</td>
                                                    <td className={`px-4 py-3 text-center font-bold ${diff === 0 ? 'text-gray-400' : diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                                        {diff > 0 ? `+${diff}` : diff}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${log.status === 'Accepted' ? 'bg-emerald-100 text-emerald-700' :
                                                            log.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                                                'bg-yellow-100 text-yellow-700'
                                                            }`}>
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Modal>

                    {/* ── Draft Details Modal ── */}
                    <Modal isOpen={!!selectedDraftGroup} onClose={() => setSelectedDraftGroupId(null)} title={`Review Submission - ${selectedDraftGroup?.changed_by}`} size="2xl">
                        {selectedDraftGroup && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center bg-gray-50 p-3 rounded border">
                                    <span className="text-sm text-gray-600 font-semibold">Bulk Actions (Entire Submission):</span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={async () => {
                                                const pendingItems = selectedDraftGroup.items.filter(i => i.status === 'Pending');
                                                for (const d of pendingItems) {
                                                    await handleAcceptDraft(d.id, d.stock_id, d.new_physical_qty);
                                                }
                                                setSelectedDraftGroupId(null);
                                                if (pendingItems.length === selectedDraftGroup.items.length && pendingDrafts.filter(pd => pd.id !== selectedDraftGroup.items[0].id).length === 0) {
                                                    setShowDraftModal(false);
                                                }
                                            }}
                                            className="bg-emerald-600 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-emerald-700 transition"
                                        >
                                            ✓ Accept All Pending
                                        </button>
                                        <button
                                            onClick={async () => {
                                                const pendingItems = selectedDraftGroup.items.filter(i => i.status === 'Pending');
                                                for (const d of pendingItems) {
                                                    await handleRejectDraft(d.id);
                                                }
                                                setSelectedDraftGroupId(null);
                                                if (pendingItems.length === selectedDraftGroup.items.length && pendingDrafts.filter(pd => pd.id !== selectedDraftGroup.items[0].id).length === 0) {
                                                    setShowDraftModal(false);
                                                }
                                            }}
                                            className="bg-red-500 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-red-600 transition"
                                        >
                                            ✗ Reject All Pending
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-y-auto max-h-[50vh] border rounded-lg">
                                    <div className="w-full overflow-x-auto max-w-full">
<table className="min-w-full text-sm border-collapse">
                                        <thead className="sticky top-0 bg-gray-100 shadow-sm border-b">
                                            <tr className="text-gray-500 text-xs uppercase font-bold">
                                                <th className="px-4 py-3 text-left">Product</th>
                                                <th className="px-4 py-3 text-center">Sys Qty</th>
                                                <th className="px-4 py-3 text-center">New Qty</th>
                                                <th className="px-4 py-3 text-center">Diff</th>
                                                <th className="px-4 py-3 text-center">Status</th>
                                                <th className="px-4 py-3 text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {selectedDraftGroup.items.map(d => {
                                                const product = stocks.find(s => String(s.id) === String(d.stock_id)) || d;
                                                const diff = Number(d.new_physical_qty) - Number(d.old_physical_qty);
                                                const isUpdated = diff !== 0;
                                                return (
                                                    <tr key={d.id} className={`hover:bg-gray-50 transition ${isUpdated ? 'bg-indigo-50/30' : 'bg-white'}`}>
                                                        <td className="px-4 py-3 font-semibold text-gray-800">{product?.product_name || `Stock #${d.stock_id}`}</td>
                                                        <td className="px-4 py-3 text-center text-gray-500">{Number(d.old_physical_qty)}</td>
                                                        <td className="px-4 py-3 text-center font-bold text-indigo-700">{Number(d.new_physical_qty)}</td>
                                                        <td className={`px-4 py-3 text-center font-bold ${diff === 0 ? 'text-gray-400' : diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                                            {diff > 0 ? `+${diff}` : diff}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${d.status === 'Accepted' ? 'bg-emerald-100 text-emerald-700' :
                                                                d.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                                                    'bg-yellow-100 text-yellow-700'
                                                                }`}>
                                                                {d.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-center flex gap-1 justify-center items-center h-full min-h-[48px]">
                                                            {d.status === 'Pending' ? (
                                                                <>
                                                                    <button
                                                                        onClick={async () => {
                                                                            await handleAcceptDraft(d.id, d.stock_id, d.new_physical_qty);
                                                                            // Update pending drafts state so it re-renders
                                                                            setPendingDrafts(prev => prev.map(p => p.id === d.id ? { ...p, status: 'Accepted' } : p));
                                                                        }}
                                                                        className="bg-emerald-600 text-white px-2 py-1 rounded text-[10px] font-bold hover:bg-emerald-700"
                                                                    >
                                                                        Accept
                                                                    </button>
                                                                    <button
                                                                        onClick={async () => {
                                                                            await handleRejectDraft(d.id);
                                                                            setPendingDrafts(prev => prev.map(p => p.id === d.id ? { ...p, status: 'Rejected' } : p));
                                                                        }}
                                                                        className="bg-red-500 text-white px-2 py-1 rounded text-[10px] font-bold hover:bg-red-600"
                                                                    >
                                                                        Reject
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <span className="text-gray-400 text-[10px] italic">Processed</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
</div>
                                </div>
                            </div>
                        )}
                    </Modal>

                </>
            )}

            {/* ── Print Container (hidden on screen, shown on print) ── */}
            {isPrinting && (
                <div className="print-container hidden print:block" ref={printRef}>
                    <div style={{ fontFamily: 'Arial, sans-serif', padding: '10mm' }}>
                        {/* Report Header */}
                        <div style={{ borderBottom: '3px solid #4f46e5', paddingBottom: '12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontSize: '20px', fontWeight: 900, color: '#312e81' }}>INVENTORY CONTROL REPORT</div>
                                <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>Warehouse: {depoName} — Generated on: {new Date().toLocaleString()}</div>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="w-full overflow-x-auto max-w-full">
<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', marginBottom: '20px' }}>
                            <thead>
                                <tr style={{ background: '#eef2ff', color: '#4338ca' }}>
                                    {['ID', 'Product', 'SKU', 'Depo', 'System Qty', 'Physical Qty', 'Diff', 'Price', 'Total', 'Vrai/Faux', 'Missing Value'].map(h => (
                                        <th key={h} style={{ border: '1px solid #e5e7eb', padding: '5px 8px', textAlign: h === 'Product' || h === 'SKU' || h === 'Depo' ? 'left' : 'center', fontWeight: 700 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {stocks.map((s, i) => {
                                    const sys = Number(s.quantity);
                                    const phyRaw = physicalQty[s.id];
                                    const phy = phyRaw !== undefined ? Number(phyRaw) : sys;
                                    const diff = phy - sys;
                                    const price = parseFloat(s.purchase_price || 0);
                                    const total = sys * price;
                                    const isMatch = sys === phy;
                                    const missingVal = sys > phy ? (sys - phy) * price : 0;
                                    return (
                                        <tr key={s.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'center', color: '#9ca3af' }}>{s.id}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', fontWeight: 600 }}>{s.product_name}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', color: '#9ca3af' }}>{s.sku || '—'}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px' }}>{depoName}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'center', fontWeight: 700, color: '#1d4ed8' }}>{sys}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'center', fontWeight: 700 }}>{phy}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'center', fontWeight: 700, color: isMatch ? '#6b7280' : diff > 0 ? '#2563eb' : '#dc2626' }}>{diff > 0 ? `+${diff}` : diff}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'right' }}>CFA{price.toFixed(2)}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>CFA{total.toFixed(2)}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'center', fontWeight: 700, color: isMatch ? '#059669' : '#dc2626' }}>{isMatch ? 'Vrai' : 'Faux'}</td>
                                            <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px', textAlign: 'right', color: missingVal > 0 ? '#dc2626' : '#6b7280', fontWeight: missingVal > 0 ? 700 : 400 }}>CFA{missingVal.toFixed(2)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
</div>

                        {/* Loss Summary */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ border: '2px solid #4f46e5', borderRadius: '8px', padding: '16px', minWidth: '300px', background: '#eef2ff' }}>
                                <div style={{ fontSize: '12px', fontWeight: 900, color: '#3730a3', borderBottom: '1px solid #c7d2fe', paddingBottom: '8px', marginBottom: '10px' }}>INVENTORY CONTROL SUMMARY</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '10px' }}>
                                    <span>Total System Stock Value</span>
                                    <strong>${totalSystemValue.toFixed(2)}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '10px' }}>
                                    <span>Total Physical Count Value</span>
                                    <strong style={{ color: '#059669' }}>${totalPhysicalValue.toFixed(2)}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '10px', borderTop: '1px solid #c7d2fe', paddingTop: '6px' }}>
                                    <span><strong>Missing / Loss Value</strong></span>
                                    <strong style={{ color: '#dc2626' }}>${totalMissingValue.toFixed(2)}</strong>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                                    <span>Loss %</span>
                                    <strong style={{ color: '#ea580c' }}>{lossPercent}%</strong>
                                </div>
                                {missingItems.length > 0 && (
                                    <>
                                        <div style={{ marginTop: '12px', fontSize: '10px', fontWeight: 700, color: '#b91c1c', borderTop: '1px solid #c7d2fe', paddingTop: '8px' }}>
                                            MISSING PRODUCTS ({missingItems.length}):
                                        </div>
                                        {missingItems.map(s => {
                                            const sys = Number(s.quantity);
                                            const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : sys;
                                            const missing = sys - phy;
                                            const price = parseFloat(s.purchase_price || 0);
                                            return (
                                                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#7f1d1d', marginTop: '4px' }}>
                                                    <span>{s.product_name} (-{missing})</span>
                                                    <span>${(missing * price).toFixed(2)}</span>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ControlInventory;
